/**
 * AppErrorBoundary.tsx
 *
 * Global last-resort error boundary. Wraps the entire React tree in main.tsx.
 *
 * If ANY component throws during rendering, React 18 would otherwise unmount
 * the entire tree — leaving the user with a blank screen and no recovery path.
 * This boundary:
 *   1. Captures the error and fires a best-effort POST to /api/client-errors.
 *   2. Renders a branded WorkRoamer recovery screen (never a blank page).
 *   3. Never exposes raw stack traces to the user.
 *
 * Design requirements (Build 2.29.0):
 *   • Show "WorkRoamer encountered an unexpected error."
 *   • Show "A diagnostic report has already been sent."
 *   • Retry button   — clears error state, re-renders children.
 *   • Return to Login — hard-navigates to /login (clears corrupted state).
 *   • Reload Application — hard-reloads the page (clears JS heap).
 *   • POST: full exception + component stack + browser info + route + userId + timestamp.
 *   • Never expose raw stack traces to users.
 */

import React from 'react';
import { reportError } from '@/services/ErrorTelemetry';

interface BoundaryState {
  hasError: boolean;
  reported: boolean;
}

export class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  BoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, reported: false };
  }

  static getDerivedStateFromError(): Partial<BoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Log to console for DevTools inspection (never shown to user).
    console.error('[AppErrorBoundary] Caught render error:', error);
    console.error('[AppErrorBoundary] Component stack:', info?.componentStack);

    // Fire the telemetry POST via the ErrorTelemetry singleton.
    // reportError() is best-effort and never throws.
    reportError(error, info?.componentStack ?? '');
    this.setState({ reported: true });
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, reported: false });
  };

  private handleReturnToLogin = (): void => {
    window.location.href = '/login';
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    const BLUE_BG: React.CSSProperties = {
      position:       'fixed',
      inset:          0,
      zIndex:         999999,
      background:     'linear-gradient(160deg, #03050f 0%, #04112b 50%, #030d1f 100%)',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      padding:        '32px 24px',
      fontFamily:     "'Geist', 'Inter', system-ui, sans-serif",
      boxSizing:      'border-box',
      overflowY:      'auto',
    };

    const CARD: React.CSSProperties = {
      width:       '100%',
      maxWidth:    420,
      background:  'rgba(255,255,255,0.04)',
      border:      '1px solid rgba(255,255,255,0.09)',
      borderRadius: 16,
      padding:     '28px 24px',
      marginBottom: 24,
      boxSizing:   'border-box',
      textAlign:   'center',
    };

    const BTN_PRIMARY: React.CSSProperties = {
      width:        '100%',
      padding:      '14px 24px',
      borderRadius: 12,
      border:       'none',
      background:   'linear-gradient(90deg,#1637c0,#2563eb)',
      color:        '#fff',
      fontFamily:   'inherit',
      fontSize:     13,
      fontWeight:   700,
      letterSpacing:'0.06em',
      cursor:       'pointer',
      boxShadow:    '0 4px 20px rgba(37,99,235,0.35)',
    };

    const BTN_GHOST: React.CSSProperties = {
      width:        '100%',
      padding:      '13px 24px',
      borderRadius: 12,
      border:       '1px solid rgba(255,255,255,0.12)',
      background:   'rgba(255,255,255,0.04)',
      color:        'rgba(255,255,255,0.55)',
      fontFamily:   'inherit',
      fontSize:     13,
      fontWeight:   600,
      letterSpacing:'0.04em',
      cursor:       'pointer',
    };

    return (
      <div style={BLUE_BG}>

        {/* Logo */}
        <img
          src="/workroamer-brand.png"
          alt="WorkRoamer™"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          style={{
            width:        140,
            maxWidth:     '60%',
            height:       'auto',
            marginBottom: 32,
            opacity:      0.88,
          }}
        />

        {/* Main card */}
        <div style={CARD}>

          {/* Alert badge */}
          <div style={{
            display:        'inline-flex',
            alignItems:     'center',
            gap:            8,
            background:     'rgba(239,68,68,0.12)',
            border:         '1px solid rgba(239,68,68,0.30)',
            borderRadius:   8,
            padding:        '6px 14px',
            marginBottom:   20,
          }}>
            <span style={{ fontSize: 14 }}>⚠️</span>
            <span style={{
              fontSize:      10,
              fontWeight:    700,
              letterSpacing: '0.16em',
              color:         '#f87171',
              textTransform: 'uppercase',
            }}>
              Unexpected Error
            </span>
          </div>

          {/* Primary message */}
          <h2 style={{
            margin:      '0 0 10px',
            fontSize:    18,
            fontWeight:  700,
            color:       'rgba(255,255,255,0.92)',
            lineHeight:  1.35,
          }}>
            WorkRoamer encountered an unexpected error.
          </h2>

          {/* Diagnostic confirmation */}
          <p style={{
            margin:     '0 0 6px',
            fontSize:   13,
            color:      'rgba(255,255,255,0.45)',
            lineHeight: 1.55,
          }}>
            A diagnostic report has already been sent.
          </p>

          <p style={{
            margin:     0,
            fontSize:   12,
            color:      'rgba(255,255,255,0.28)',
            lineHeight: 1.5,
          }}>
            No personal data is included in the report.
          </p>
        </div>

        {/* Action buttons */}
        <div style={{
          display:       'flex',
          flexDirection: 'column',
          gap:           10,
          width:         '100%',
          maxWidth:      380,
        }}>
          <button onClick={this.handleRetry} style={BTN_PRIMARY}>
            ↺ &nbsp;Retry
          </button>

          <button onClick={this.handleReload} style={BTN_GHOST}>
            ⟳ &nbsp;Reload Application
          </button>

          <button onClick={this.handleReturnToLogin} style={BTN_GHOST}>
            ← &nbsp;Return to Login
          </button>
        </div>

        {/* Footer hint */}
        <p style={{
          marginTop:  28,
          fontSize:   11,
          color:      'rgba(255,255,255,0.18)',
          textAlign:  'center',
          maxWidth:   320,
          lineHeight: 1.6,
        }}>
          If this keeps happening, try clearing your browser cache
          or contact&nbsp;support at workroamer.com.
        </p>
      </div>
    );
  }
}
